import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, ChevronLeft, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function nextDays(count) {
    const out = [];
    const today = new Date();
    for (let i = 0; i < count; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        out.push(d);
    }
    return out;
}

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const BookingModal = ({ open, onClose, mentors, preselect }) => {
    const [step, setStep] = useState(1);
    const [mentor, setMentor] = useState(null);
    const [dateStr, setDateStr] = useState(null);
    const [slots, setSlots] = useState([]);
    const [slot, setSlot] = useState(null);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", whatsapp: "", note: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const days = useMemo(() => nextDays(14), []);

    useEffect(() => {
        if (open) {
            setStep(preselect ? 2 : 1);
            setMentor(preselect || null);
            setDateStr(null);
            setSlot(null);
            setSlots([]);
            setForm({ name: "", email: "", whatsapp: "", note: "" });
            setDone(false);
        }
    }, [open, preselect]);

    useEffect(() => {
        if (!mentor || !dateStr) return;
        setLoadingSlots(true);
        setSlot(null);
        api
            .get(`/mentors/${mentor.id}/availability`, { params: { date: dateStr } })
            .then(({ data }) => setSlots(data.slots))
            .catch(() => setSlots([]))
            .finally(() => setLoadingSlots(false));
    }, [mentor, dateStr]);

    const submit = async () => {
        if (!form.name || !form.email || !form.whatsapp) {
            toast.error("Completa nombre, email y WhatsApp");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/bookings", {
                mentor_id: mentor.id,
                date: dateStr,
                time: slot,
                ...form,
            });
            setDone(true);
            toast.success("¡Llamada agendada!");
        } catch (e) {
            toast.error(apiErr(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center"
                    onClick={onClose}
                    data-testid="booking-modal"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.4 }}
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-border bg-card p-6 sm:rounded-3xl"
                    >
                        <div className="mb-5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                {step > 1 && !done && !preselect && (
                                    <button
                                        onClick={() => setStep((s) => s - 1)}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid="booking-back"
                                    >
                                        <ChevronLeft size={20} />
                                    </button>
                                )}
                                <h3 className="text-lg font-bold text-foreground">Agendar llamada</h3>
                            </div>
                            <button onClick={onClose} data-testid="booking-close" className="text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </button>
                        </div>

                        {done ? (
                            <div className="py-8 text-center">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
                                    <Check size={32} />
                                </div>
                                <h4 className="mt-4 text-xl font-bold text-foreground">¡Listo, {form.name}!</h4>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    Tu llamada con <span className="text-primary">{mentor.display_name}</span> quedó agendada para el{" "}
                                    <span className="text-foreground">{dateStr}</span> a las{" "}
                                    <span className="text-foreground">{slot}</span>.
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">Te contactaremos por WhatsApp para confirmar.</p>
                                <button
                                    onClick={onClose}
                                    className="mt-6 rounded-full bg-primary px-6 py-2.5 text-sm font-bold uppercase tracking-widest text-primary-foreground"
                                    data-testid="booking-finish"
                                >
                                    Cerrar
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Step 1: choose mentor */}
                                {step === 1 && (
                                    <div className="space-y-2" data-testid="booking-step-mentor">
                                        <p className="mb-3 text-sm text-muted-foreground">Elige a tu mentor:</p>
                                        {mentors.map((m) => (
                                            <button
                                                key={m.id}
                                                onClick={() => {
                                                    setMentor(m);
                                                    setStep(2);
                                                }}
                                                data-testid={`select-mentor-${m.username}`}
                                                className="flex w-full items-center gap-3 rounded-xl border border-border p-3 text-left transition-all hover:border-primary/60 hover:bg-white/5"
                                            >
                                                <img src={m.photo_url || "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=200"} alt={m.display_name} loading="lazy" decoding="async" className="h-12 w-12 rounded-full object-cover" />
                                                <div>
                                                    <p className="font-semibold text-foreground">{m.display_name}</p>
                                                    <p className="text-xs text-primary">{m.specialty}</p>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Step 2: choose date & slot */}
                                {step === 2 && mentor && (
                                    <div data-testid="booking-step-slot">
                                        <p className="mb-3 text-sm text-muted-foreground">
                                            Con <span className="font-semibold text-foreground">{mentor.display_name}</span> — elige día y hora:
                                        </p>
                                        <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
                                            {days.map((d) => {
                                                const ds = fmt(d);
                                                const active = ds === dateStr;
                                                return (
                                                    <button
                                                        key={ds}
                                                        onClick={() => setDateStr(ds)}
                                                        data-testid={`date-${ds}`}
                                                        className={`flex min-w-[56px] flex-col items-center rounded-xl border px-2 py-2 transition-all ${
                                                            active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                                                        }`}
                                                    >
                                                        <span className="text-[10px] uppercase">{WEEKDAYS[(d.getDay() + 6) % 7]}</span>
                                                        <span className="text-base font-bold">{d.getDate()}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {dateStr && (
                                            <div className="min-h-[80px]">
                                                {loadingSlots ? (
                                                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-primary" /></div>
                                                ) : slots.length === 0 ? (
                                                    <p className="py-6 text-center text-sm text-muted-foreground">Sin horarios disponibles este día.</p>
                                                ) : (
                                                    <div className="grid grid-cols-4 gap-2">
                                                        {slots.map((s) => (
                                                            <button
                                                                key={s}
                                                                onClick={() => setSlot(s)}
                                                                data-testid={`slot-${s}`}
                                                                className={`rounded-lg border py-2 text-sm font-semibold transition-all ${
                                                                    slot === s ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:border-primary/50"
                                                                }`}
                                                            >
                                                                {s}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <button
                                            disabled={!slot}
                                            onClick={() => setStep(3)}
                                            data-testid="booking-to-details"
                                            className="mt-5 w-full rounded-full bg-primary py-3 text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Continuar
                                        </button>
                                    </div>
                                )}

                                {/* Step 3: details */}
                                {step === 3 && (
                                    <div className="space-y-3" data-testid="booking-step-details">
                                        <p className="text-sm text-muted-foreground">
                                            {mentor.display_name} · {dateStr} · {slot}
                                        </p>
                                        {[
                                            { k: "name", ph: "Tu nombre", type: "text" },
                                            { k: "email", ph: "Tu email", type: "email" },
                                            { k: "whatsapp", ph: "Tu WhatsApp (con código país)", type: "tel" },
                                        ].map((f) => (
                                            <input
                                                key={f.k}
                                                type={f.type}
                                                placeholder={f.ph}
                                                value={form[f.k]}
                                                data-testid={`booking-input-${f.k}`}
                                                onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                                                className="w-full rounded-xl border border-input bg-secondary px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                                            />
                                        ))}
                                        <textarea
                                            placeholder="¿Qué estás intentando vender? (opcional)"
                                            value={form.note}
                                            data-testid="booking-input-note"
                                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                                            rows={2}
                                            className="w-full resize-none rounded-xl border border-input bg-secondary px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
                                        />
                                        <button
                                            onClick={submit}
                                            disabled={submitting}
                                            data-testid="booking-submit"
                                            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:brightness-110 disabled:opacity-50"
                                        >
                                            {submitting && <Loader2 size={16} className="animate-spin" />}
                                            Confirmar llamada
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
